import { RichText } from "~/components/ui/rich-text"
import { HiCheck } from "react-icons/hi2"
import type { Plan } from "@apps/gwservice/types"
import { ApplicationButton } from "./application-button"
import { CancelPendingLink } from "./cancel-pending-link"
import { ChangeCardLink } from "./change-card-link"
import { DeleteCardLink } from "./delete-card-link"
import { SubscribeButton } from "./subscribe-button"
import { UnsubscribeButton } from "./unsubscribe-button"

const fmt = (n: number) => n.toLocaleString("ru-RU")
const fmtDate = (s: string) =>
  new Date(s).toLocaleDateString("ru-RU", {
    day: "numeric",
    month: "long",
    year: "numeric",
  })

function PaymentButtons({ plan, disabled }: { plan: Plan; disabled: boolean }) {
  if (plan.requiresApproval) {
    return (
      <div className="flex justify-center">
        <ApplicationButton plan={plan} />
      </div>
    )
  }
  return (
    <div className={`flex gap-m ${plan.priceMonth ? "" : "justify-center"}`}>
      {plan.priceMonth && (
        <SubscribeButton planId={plan.id} period="monthly" variant="outline" className="flex-1" disabled={disabled}>
          {fmt(plan.priceMonth)} ₽ / мес
        </SubscribeButton>
      )}
      <SubscribeButton planId={plan.id} period="yearly" className={plan.priceMonth ? "flex-1" : "px-xl"} disabled={disabled}>
        {fmt(plan.priceYearDiscount)} ₽ / год
        {plan.priceYear && <s className="opacity-50 ml-s">{fmt(plan.priceYear)} ₽</s>}
      </SubscribeButton>
    </div>
  )
}

export function PlanCard({ plan, hasPendingMethod }: { plan: Plan; hasPendingMethod: boolean }) {
  const isCurrent = !!plan.chargeDate
  const isUnsubscribed = isCurrent && !plan.chargeAmount
  const hasPending = !!plan.pendingPayment
  const showBindingHint = hasPendingMethod && plan.renderChangeMethod && !hasPending

  return (
    <div className="flex flex-col gap-m px-m">
      <div className="flex items-center gap-s justify-between">
        <h2 className="text-xl font-bold">Тариф «{plan.name}»</h2>
        {isCurrent && (
          <div className="flex items-center gap-xs text-secondary font-medium text-sm">
            <HiCheck className="size-4" /> Ваш тариф
          </div>
        )}
      </div>
      <RichText data={plan.description} />
      {isCurrent && (
        <div className="flex flex-col gap-s">
          {plan.chargeAmount ? (
            <>
              {plan.chargeDate && (
                <p className="text-sm text-secondary">
                  {fmt(plan.chargeAmount)} ₽ будет списано {fmtDate(plan.chargeDate)}
                </p>
              )}
              <div className="flex flex-wrap gap-m items-center justify-between">
                <UnsubscribeButton />
                {plan.renderChangeMethod && <ChangeCardLink methodName={plan.paymentMethodName} disabled={hasPendingMethod} />}
              </div>
              {showBindingHint && (
                <div className="flex flex-wrap items-center gap-m justify-between">
                  <p className="text-sm text-secondary">Привязываем карту…</p>
                  <CancelPendingLink kind="binding" />
                </div>
              )}
            </>
          ) : (
            plan.chargeDate && <p className="text-sm text-muted-foreground">Оплачено до {fmtDate(plan.chargeDate)}</p>
          )}
        </div>
      )}
      {(!isCurrent || isUnsubscribed) && (
        <div className="flex flex-col gap-s">
          <PaymentButtons plan={plan} disabled={hasPending} />
          {hasPending && plan.pendingPayment && (
            <div className="flex flex-wrap items-center gap-m justify-between">
              <p className="text-sm text-secondary">Ожидаем подтверждение банка…</p>
              <CancelPendingLink kind="payment" />
            </div>
          )}
          {isUnsubscribed && plan.renderChangeMethod && (
            <div className="flex flex-wrap gap-m items-center justify-between">
              <DeleteCardLink disabled={hasPendingMethod} />
              <ChangeCardLink methodName={plan.paymentMethodName} disabled={hasPendingMethod} />
            </div>
          )}
          {isUnsubscribed && showBindingHint && (
            <div className="flex flex-wrap items-center gap-m justify-between">
              <p className="text-sm text-secondary">Привязываем карту…</p>
              <CancelPendingLink kind="binding" />
            </div>
          )}
        </div>
      )}
    </div>
  )
}
